import React, { useState, useEffect, useRef } from "react";
import { Search, Replace, ChevronLeft, ChevronRight, X } from "lucide-react";

interface CATSegment {
    id: string;
    source_text: string;
    target_text: string;
    segment_order: number;
    status:
        | "new"
        | "in_progress"
        | "completed"
        | "approved"
        | "confirmed"
        | "rejected";
    locked: boolean;
    notes: string;
    created_at: string;
    updated_at: string;
}

interface FindReplaceBarProps {
    segments: CATSegment[];
    onReplaceOne: (segmentId: string, newText: string) => void;
    onReplaceAll: (replacements: Record<string, string>) => void;
    onClose: () => void;
    onMatchChange?: (segmentId: string) => void;
    onFindTextChange?: (text: string) => void;
    onScopeChange?: (scope: "target" | "source") => void;
}

const FindReplaceBar: React.FC<FindReplaceBarProps> = ({
    segments,
    onReplaceOne,
    onReplaceAll,
    onClose,
    onMatchChange,
    onFindTextChange,
    onScopeChange,
}) => {
    const [findText, setFindText] = useState("");
    const [replaceText, setReplaceText] = useState("");
    const [matchCase, setMatchCase] = useState(false);
    const [wholeWord, setWholeWord] = useState(false);
    const [scope, setScope] = useState<"target" | "source">("target");
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const [matches, setMatches] = useState<
        Array<{ id: string; segmentIndex: number; text: string }>
    >([]);
    const findInputRef = useRef<HTMLInputElement>(null);

    // Find matches whenever findText, matchCase, scope, or segments change
    useEffect(() => {
        if (!findText.trim()) {
            setMatches([]);
            setCurrentMatchIndex(0);
            onFindTextChange?.(findText);
            return;
        }

        const newMatches: Array<{
            id: string;
            segmentIndex: number;
            text: string;
        }> = [];

        segments.forEach((segment, index) => {
            const textToSearch =
                scope === "target" ? segment.target_text : segment.source_text;
            if (!textToSearch) return;

            const pattern = wholeWord
                ? `\\b${findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`
                : findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const searchFlags = matchCase ? "g" : "gi";
            const regex = new RegExp(pattern, searchFlags);
            const match = textToSearch.match(regex);

            if (match) {
                newMatches.push({
                    id: segment.id,
                    segmentIndex: index,
                    text: textToSearch,
                });
            }
        });

        setMatches(newMatches);
        setCurrentMatchIndex(newMatches.length > 0 ? 0 : 0);
        if (newMatches.length > 0) onMatchChange?.(newMatches[0].id);
        onFindTextChange?.(findText);
    }, [findText, matchCase, scope, segments, wholeWord]);

    // Focus find input on mount
    useEffect(() => {
        if (findInputRef.current) {
            findInputRef.current.focus();
        }
    }, []);

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onClose();
            }
            if ((e.ctrlKey || e.metaKey) && e.key === "g") {
                e.preventDefault();
                handleNextMatch();
            }
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "g") {
                e.preventDefault();
                handlePreviousMatch();
            }
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                handleReplace();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [currentMatchIndex, matches]);

    const handlePreviousMatch = () => {
        if (matches.length === 0) return;
        const prevIndex =
            currentMatchIndex === 0
                ? matches.length - 1
                : currentMatchIndex - 1;
        setCurrentMatchIndex(prevIndex);
        onMatchChange?.(matches[prevIndex]?.id);
    };

    const handleNextMatch = () => {
        if (matches.length === 0) return;
        const nextIndex =
            currentMatchIndex === matches.length - 1
                ? 0
                : currentMatchIndex + 1;
        setCurrentMatchIndex(nextIndex);
        onMatchChange?.(matches[nextIndex]?.id);
    };

    const handleReplace = () => {
        if (matches.length === 0 || currentMatchIndex >= matches.length) return;

        const match = matches[currentMatchIndex];
        const segment = segments.find((s) => s.id === match.id);
        if (!segment) return;

        const currentText =
            scope === "target" ? segment.target_text : segment.source_text;
        const pattern = wholeWord
            ? `\\b${findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`
            : findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const searchFlags = matchCase ? "g" : "gi";
        const regex = new RegExp(pattern, searchFlags);
        const newText = currentText.replace(regex, replaceText);

        // Only call onReplaceOne for target scope (as per requirements)
        if (scope === "target") {
            onReplaceOne(match.id, newText);
        }

        // After replacement, find next match
        handleNextMatch();
    };

    const handleReplaceAll = () => {
        if (matches.length === 0) return;

        const replacements: Record<string, string> = {};

        matches.forEach((match) => {
            const segment = segments.find((s) => s.id === match.id);
            if (!segment) return;

            const currentText =
                scope === "target" ? segment.target_text : segment.source_text;
            const pattern = wholeWord
                ? `\\b${findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`
                : findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const searchFlags = matchCase ? "g" : "gi";
            const regex = new RegExp(pattern, searchFlags);
            const newText = currentText.replace(regex, replaceText);

            // Only add to replacements for target scope (as per requirements)
            if (scope === "target") {
                replacements[match.id] = newText;
            }
        });

        // Only call onReplaceAll if we have target replacements
        if (Object.keys(replacements).length > 0) {
            onReplaceAll(replacements);
        }
    };

    const matchCounterText =
        matches.length > 0
            ? `${currentMatchIndex + 1} of ${matches.length} matches`
            : "No matches";

    return (
        <div className="flex items-center gap-2 p-2 bg-slate-800 border-b border-slate-700 shadow-lg">
            {/* Find input */}
            <div className="relative flex-1 max-w-xs">
                <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                    ref={findInputRef}
                    type="text"
                    placeholder="Find"
                    value={findText}
                    onChange={(e) => {
                        setFindText(e.target.value);
                        onFindTextChange?.(e.target.value);
                    }}
                    className={`pl-7 pr-2 py-1.5 border rounded text-xs w-full focus:outline-none focus:ring-1 focus:ring-gold-500 bg-slate-700 text-white placeholder-slate-500 ${
                        findText && matches.length === 0
                            ? "border-red-500"
                            : "border-slate-600"
                    }`}
                />
            </div>

            {/* Replace input */}
            <div className="relative flex-1 max-w-xs">
                <Replace className="h-3.5 w-3.5 absolute left-2 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                    type="text"
                    placeholder="Replace"
                    value={replaceText}
                    onChange={(e) => setReplaceText(e.target.value)}
                    className="pl-7 pr-2 py-1.5 border border-slate-600 rounded text-xs w-full focus:outline-none focus:ring-1 focus:ring-gold-500 bg-slate-700 text-white placeholder-slate-500"
                />
            </div>

            {/* Match case checkbox */}
            <div className="flex items-center gap-1">
                <input
                    type="checkbox"
                    id="match-case"
                    checked={matchCase}
                    onChange={(e) => setMatchCase(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-700 text-gold-500 focus:ring-gold-500 focus:ring-1"
                />
                <label
                    htmlFor="match-case"
                    className="text-xs text-slate-300 whitespace-nowrap"
                >
                    Match case
                </label>
            </div>

            {/* Whole word checkbox */}
            <div className="flex items-center gap-1">
                <input
                    type="checkbox"
                    id="whole-word"
                    checked={wholeWord}
                    onChange={(e) => setWholeWord(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-700 text-gold-500 focus:ring-gold-500 focus:ring-1"
                />
                <label
                    htmlFor="whole-word"
                    className="text-xs text-slate-300 whitespace-nowrap"
                >
                    Whole word
                </label>
            </div>

            {/* Scope dropdown */}
            <select
                value={scope}
                onChange={(e) => {
                    const newScope = e.target.value as "target" | "source";
                    setScope(newScope);
                    onScopeChange?.(newScope);
                }}
                className="border border-slate-600 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-gold-500 bg-slate-700 text-white"
            >
                <option value="target">Target text</option>
                <option value="source">Source text</option>
            </select>

            {/* Match counter */}
            <div
                className={`text-xs px-2 py-1.5 rounded ${matches.length > 0 ? "text-slate-300" : "text-slate-500"}`}
            >
                {matchCounterText}
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center gap-1">
                <button
                    onClick={handlePreviousMatch}
                    disabled={matches.length === 0}
                    className="p-1.5 rounded text-slate-300 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors border border-slate-600"
                    title="Previous match (Ctrl+Shift+G)"
                >
                    <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <button
                    onClick={handleNextMatch}
                    disabled={matches.length === 0}
                    className="p-1.5 rounded text-slate-300 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors border border-slate-600"
                    title="Next match (Ctrl+G)"
                >
                    <ChevronRight className="h-3.5 w-3.5" />
                </button>
            </div>

            {/* Replace buttons */}
            <div className="flex items-center gap-1">
                <button
                    onClick={handleReplace}
                    disabled={
                        !replaceText.trim() ||
                        matches.length === 0 ||
                        scope !== "target"
                    }
                    className="px-2.5 py-1.5 text-xs font-medium rounded transition-all border border-gold-600 text-gold-100 bg-gold-700 hover:bg-gold-600 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Replace current match (Ctrl+Enter)"
                >
                    Replace
                </button>
                <button
                    onClick={handleReplaceAll}
                    disabled={
                        !replaceText.trim() ||
                        matches.length === 0 ||
                        scope !== "target"
                    }
                    className="px-2.5 py-1.5 text-xs font-medium rounded transition-all border border-gold-600 text-gold-100 bg-gold-700 hover:bg-gold-600 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Replace all matches"
                >
                    Replace All
                </button>
            </div>

            {/* Close button */}
            <button
                onClick={onClose}
                className="p-1.5 rounded text-slate-300 hover:bg-slate-700 transition-colors"
                title="Close (Esc)"
            >
                <X className="h-3.5 w-3.5" />
            </button>
        </div>
    );
};

export default FindReplaceBar;
